import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * Front-end permission reflection — the UX half of the separation-of-duties
 * model. The backend still enforces per-route (WorkOS identity + app
 * authorization); this hook decides whether an action *control* renders, so
 * the UI shows the SoD model rather than offering a button the server will
 * reject.
 *
 * Source: GET /api/me/permissions -> { user, permissions: { <entity>:
 * {can_view,can_add,can_edit,can_delete,can_process} } }. super_admin comes
 * back all-true; other roles reflect their security group's grid. Cached per
 * session (staleTime 5m) and shared across every gated surface.
 *
 * can_process is the workflow/PROCESS capability (the server maps the PROCESS
 * verb to it) — gate workflow actions (file-appeal, record-outcome, invoice-all,
 * tax-bill lifecycle) on it, NOT on can_add/can_edit, so the control renders iff
 * the endpoint would authorize it.
 *
 * PRD domain 04 calls for this hook to exist before the money/SoD surfaces
 * (04 invoicing, 05 payments, 12 valuation, 03 RBAC) that gate on it.
 */

export type PermissionAction =
  | 'can_view' | 'can_add' | 'can_edit' | 'can_delete' | 'can_process' | 'can_export';

export interface EntityPermissions {
  can_view: boolean;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_process: boolean;
  /**
   * The EXPORT capability — what `exportGate` enforces on external-ledger sync
   * (QuickBooks push) and report egress. Gating an export control on `can_edit`
   * instead UNDER-exposes it: an agent holds invoice.can_export but not
   * invoice.can_edit, so the control was hidden from a role the server authorizes.
   *
   * OPTIONAL on purpose — the bundle and the server do not ship atomically, so new
   * code can be talking to a server that predates this field. Marking it required
   * would make the type assert something the wire cannot guarantee;
   * `useEntityPermission` already resolves a missing value to `false` (default-deny).
   */
  can_export?: boolean;
}

export interface MePermissions {
  user: {
    id: string;
    email: string;
    role: string;
    contact_id: string | null;
    security_group_id: string | null;
  };
  permissions: Record<string, EntityPermissions>;
}

/** Fetch the current user's permission grid (cached, session-stable). */
export function usePermissions() {
  return useQuery<MePermissions>({
    queryKey: ['me-permissions'],
    queryFn: ({ signal }) => api.get<MePermissions>('/api/me/permissions', signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * Does the current user hold `action` on `entity`? `entity` is the canonical
 * singular grid name (e.g. 'parcel', 'invoice', 'client').
 *
 * Default-DENY: returns false while the grid is loading or on error, so an
 * action control never renders speculatively. The UI renders the SoD model;
 * it never softens it (ENGINEERING_STANDARDS "Agent-native UI" / global FE
 * convention 4b).
 */
export function useEntityPermission(entity: string, action: PermissionAction): boolean {
  const { data } = usePermissions();
  // Crash-safe: the `?.` must guard BOTH data AND data.permissions. Without the second
  // `?.`, a present-but-malformed response (a defined object with no `permissions` key —
  // e.g. an error envelope, or a partial payload) makes `data.permissions[entity]` throw
  // → every gated page white-screens. Default-DENY on any unexpected shape.
  return data?.permissions?.[entity]?.[action] ?? false;
}
